import { useSetAtom } from 'jotai';
import { ReactNode } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Link, Modal, ModalBody, ModalFooter, ModalHeader, Text, cn } from '@growtherapy/sprout-ui';

import { AudioVideoTroubleshootActionItemWrapper as BaseAudioVideoTroubleshootActionItem } from '../components/help-modal/AudioVideoTroubleshootActionItem';
import { HelpModalItem } from '../components/help-modal/HelpModalItem';
import { CLIENT_TELEHEALTH_HELP_ARTICLE } from '../components/help-modal/constants';
import { useTheme } from '../hooks';
import { Translate } from '../i18n/Translate';
import { useInitAvailableDevices } from '../session/devices/useDevices';
import { Theme } from '../state';
import { Appointment } from '../types';
import { useContactUs } from '../useContactUs';
import { ProviderNotHereDescriptionWrapper as ProviderNotHereDescription } from './ProviderNotHereDescription';
import { VisitorDrawerState, visitorDrawerStateAtom } from './state';
import { useRecordProviderNoShowReportCallback } from './waiting-room/useRecordProviderNoShowReportCallback';

type HelpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appointment?: Pick<Appointment, 'hasProviderNoShowReport'> | null;
  audioVideoTroubleshootActionItem?: ReactNode;
  shouldShowProviderNoShowSection?: boolean;
  isPastNoShowThreshold: boolean;
  clientJoinedAfterWindowToReport: boolean;
  recordProviderNoShowAndRedirect: () => void;
};

export function HelpModal({
  isOpen,
  onClose,
  appointment,
  audioVideoTroubleshootActionItem: AudioVideoTroubleshootActionItem,
  shouldShowProviderNoShowSection,
  isPastNoShowThreshold,
  clientJoinedAfterWindowToReport,
  recordProviderNoShowAndRedirect,
}: HelpModalProps) {
  const t = useTranslate();
  const { pageUrl } = useContactUs();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  const supportText = (
    <Text
      variant='sm'
      className={cn({
        'rebrand:text-text-inverse': isDarkTheme,
      })}
    >
      <Translate
        i18nKey='telehealth-core:troubleshooting.ifYouNeedAdditionalSupportReachOutToUs'
        components={{
          1: (
            <Link
              href={pageUrl}
              className={cn('text-neutral-700 hover:text-neutral-900', {
                'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                  isDarkTheme,
              })}
              size='sm'
              target='_blank'
              external={true}
            />
          ),
        }}
      />
    </Text>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={cn('rebrand:rounded-none', {
        'rebrand:bg-surface-inverseEmphasis': isDarkTheme,
      })}
    >
      <ModalHeader
        contentClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      >
        {t('telehealth-core:troubleshooting.needHelp')}
      </ModalHeader>
      <ModalBody className='sm:py-8 flex flex-col gap-6'>
        <Text
          className={cn('text-neutral-800 rebrand:text-text-default', {
            'rebrand:text-text-inverse': isDarkTheme,
          })}
          as='h2'
        >
          {t('telehealth-core:troubleshooting.commonIssues')}
        </Text>
        <ol className='list-style-none flex flex-col gap-6'>
          {AudioVideoTroubleshootActionItem}
          {shouldShowProviderNoShowSection && (
            <HelpModalItem
              heading={t('telehealth-core:troubleshooting.mySessionHasntStarted')}
              description={
                <ProviderNotHereDescription
                  onClose={onClose}
                  clientHasAppointment={!!appointment}
                  isPastNoShowThreshold={isPastNoShowThreshold}
                  clientJoinedAfterWindowToReport={clientJoinedAfterWindowToReport}
                  recordProviderNoShowAndRedirect={recordProviderNoShowAndRedirect}
                  appointmentHasProviderNoShowReport={appointment?.hasProviderNoShowReport ?? false}
                />
              }
            />
          )}
          <HelpModalItem
            heading={t('telehealth-core:troubleshooting.iNeedHelpWithADifferentIssue')}
            description={
              <Translate
                i18nKey='telehealth-core:troubleshooting.seeOurTelehealthGuideForAdditionalHelp'
                components={{
                  1: (
                    <Link
                      external
                      className={cn(
                        'text-neutral-800 hover:text-neutral-900 rebrand:text-action-primary-textLink hover:rebrand:text-action-primary-textLinkHover',
                        {
                          'rebrand:text-action-primary-textLinkInverse rebrand:hover:text-action-primary-textLinkInverseHover':
                            isDarkTheme,
                        },
                      )}
                      href={CLIENT_TELEHEALTH_HELP_ARTICLE}
                    />
                  ),
                }}
              />
            }
          />
        </ol>
      </ModalBody>

      <ModalFooter
        actions={[
          {
            key: 'close-modal',
            actionContent: t('telehealth-core:troubleshooting.done'),
            onClick: onClose,
            use: 'secondary',
          },
        ]}
      >
        {supportText}
      </ModalFooter>
    </Modal>
  );
}

export function HelpModalWrapper(
  props: Omit<
    HelpModalProps,
    | 'audioVideoTroubleshootActionItem'
    | 'isPastNoShowThreshold'
    | 'clientJoinedAfterWindowToReport'
    | 'recordProviderNoShowAndRedirect'
  >,
) {
  useInitAvailableDevices();
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const openSettings = () => {
    props.onClose();
    setDrawerState(VisitorDrawerState.SETTINGS);
  };
  const { isPastNoShowThreshold, clientJoinedAfterWindowToReport, recordProviderNoShowAndRedirect } =
    useRecordProviderNoShowReportCallback();

  return (
    <HelpModal
      isPastNoShowThreshold={isPastNoShowThreshold}
      clientJoinedAfterWindowToReport={clientJoinedAfterWindowToReport}
      recordProviderNoShowAndRedirect={recordProviderNoShowAndRedirect}
      audioVideoTroubleshootActionItem={<BaseAudioVideoTroubleshootActionItem onOpenSettings={openSettings} />}
      {...props}
    />
  );
}
